import { canViewMyRelationshipsTab } from "../../Pages/Relationship/my-relationships/relationshipTabAccess";
import {
  ROLES,
  RoleType,
  getMenuItemsForRole,
  normalizeRoleName,
} from "../Constants/RoleConstants";

/**
 * Maps sidebar menu paths from ROLE_MENU_ITEMS (RoleConstants) to Guidance Hub
 * nav-link registry keys. Tabbed destinations under the same page share one
 * menu entry and expand to multiple chip keys.
 *
 * Chip visibility is derived from the role's menu — keep ROLE_MENU_ITEMS as
 * the source of truth for which pages a role can open.
 */
const MENU_PATH_TO_LINK_KEYS: Record<string, readonly string[]> = {
  "/my-portfolio-accounts": [
    "my-accounts",
    "account-priorities",
    "relationship-priorities",
  ],
  "/relationship": ["my-relationships"],
  "/always-on-channel": ["always-on"],
  "/pending-assessments": ["assessments", "levels-mix"],
  "/leaderboard": ["leaderboard"],
  "/goals-milestones": ["goals-milestones"],
};

/** Union of every mapped registry key — used for browse-only admin roles. */
const ALL_MAPPED_LINK_KEYS: string[] = Array.from(
  new Set(Object.values(MENU_PATH_TO_LINK_KEYS).flatMap(keys => [...keys]))
);

const BROWSE_ONLY_ROLES = new Set<RoleType>([
  ROLES.IZBA_ADMIN,
  ROLES.VENDOR_ADMIN,
]);

/**
 * Which nav-links-registry keys are real, navigable routes for each app role.
 *
 * Derived from ROLE_MENU_ITEMS in RoleConstants. Playbook content can still
 * reference a target another role can't reach — that chip is omitted rather
 * than producing a dead link. Browse-only roles (vendor_admin / izba_admin)
 * see all known registry keys so they can preview other roles' hubs.
 */
export const getAccessibleLinkKeysForRole = (
  userRole: string | { name?: string } | null | undefined
): string[] => {
  const normalizedRole = normalizeRoleName(userRole);
  if (!normalizedRole) return [];

  if (BROWSE_ONLY_ROLES.has(normalizedRole as RoleType)) {
    return ALL_MAPPED_LINK_KEYS;
  }

  const menuItems = getMenuItemsForRole(userRole);
  // Unknown / unmapped roles: allow every known chip key (same as prior
  // browse-all fallback) so Hub content remains visible while browsing.
  if (!menuItems.length) return ALL_MAPPED_LINK_KEYS;

  const keys = new Set<string>();
  for (const item of menuItems) {
    const mapped = MENU_PATH_TO_LINK_KEYS[item.path];
    if (!mapped) continue;
    for (const key of mapped) {
      // Menu may include /relationship even when the My Relationships tab is
      // gated (e.g. executive_sponsor_lead) — omit that chip for those roles.
      if (
        key === "my-relationships" &&
        !canViewMyRelationshipsTab(normalizedRole)
      ) {
        continue;
      }
      keys.add(key);
    }
  }
  return Array.from(keys);
};
